import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart3,
  Loader2,
  Users,
  ShoppingBag,
  Package,
  Handshake,
  TrendingUp,
  TrendingDown,
  Calendar,
  RefreshCw,
  Crown,
  Ban,
  Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple line chart component
const SimpleChart = ({ data, dataKey, color = '#C9956C', height = 120 }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1 || 1)) * 100;
    const y = 100 - (v / max) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={points}
        />
        <linearGradient id={`gradient-${dataKey}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <polygon
          fill={`url(#gradient-${dataKey})`}
          points={`0,100 ${points} 100,100`}
        />
      </svg>
    </div>
  );
};

const StatCard = ({ title, value, change, icon: Icon, color = 'blue', chart, chartData, chartKey }) => {
  const isPositive = change > 0;
  const isNegative = change < 0;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
      
      {change !== undefined && (
        <div className="flex items-center gap-1 text-sm">
          {isPositive && <TrendingUp className="w-4 h-4 text-green-500" />}
          {isNegative && <TrendingDown className="w-4 h-4 text-red-500" />}
          <span className={isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-500'}>
            {isPositive && '+'}{change}%
          </span>
          <span className="text-gray-400">vs last period</span>
        </div>
      )}
      
      {chart && chartData && (
        <div className="mt-3 -mx-2 -mb-2">
          <SimpleChart data={chartData} dataKey={chartKey} color={`var(--${color}-500, #3B82F6)`} height={60} />
        </div>
      )}
    </div>
  );
};

export default function AdminAnalyticsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [token, days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Calculate period change percentage
  const calcChange = (current, previous) => {
    if (!previous) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading && !analytics) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  const { totals, period_totals, daily } = analytics || {};
  
  // Calculate average daily metrics
  const avgDaily = daily ? {
    active_users: Math.round(daily.reduce((a, d) => a + d.active_users, 0) / daily.length),
    registrations: Math.round(daily.reduce((a, d) => a + d.registrations, 0) / daily.length * 10) / 10,
    listings: Math.round(daily.reduce((a, d) => a + d.total_listings, 0) / daily.length * 10) / 10,
    transactions: Math.round(daily.reduce((a, d) => a + d.completed_transactions, 0) / daily.length * 10) / 10,
  } : {};

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Analytics Overview
            </h1>
            <p className="text-gray-500">Platform metrics and trends</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <Button onClick={fetchAnalytics} variant="outline" className="gap-2" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Period Summary */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4A6F] rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-[#C9956C]" />
            <span className="font-medium">Period: {analytics?.start_date} to {analytics?.end_date}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-white/60 text-sm">New Registrations</p>
              <p className="text-3xl font-bold">{period_totals?.registrations || 0}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">Active Users</p>
              <p className="text-3xl font-bold">{period_totals?.active_users_unique || 0}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">Listings Created</p>
              <p className="text-3xl font-bold">{(period_totals?.wtb_created || 0) + (period_totals?.inventory_created || 0)}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">Transactions</p>
              <p className="text-3xl font-bold">{period_totals?.completed_transactions || 0}</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Users"
            value={totals?.total_users || 0}
            icon={Users}
            color="blue"
            chart={true}
            chartData={daily}
            chartKey="active_users"
          />
          <StatCard
            title="Total Listings"
            value={(totals?.total_wtb || 0) + (totals?.total_inventory || 0)}
            icon={ShoppingBag}
            color="green"
            chart={true}
            chartData={daily}
            chartKey="total_listings"
          />
          <StatCard
            title="Completed Transactions"
            value={totals?.total_completed_transactions || 0}
            icon={Handshake}
            color="purple"
            chart={true}
            chartData={daily}
            chartKey="completed_transactions"
          />
          <StatCard
            title="Premium Users"
            value={totals?.total_premium_users || 0}
            icon={Crown}
            color="amber"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Want Listings</p>
            <p className="text-xl font-bold text-gray-900">{totals?.total_wtb || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Inventory Items</p>
            <p className="text-xl font-bold text-gray-900">{totals?.total_inventory || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Total Matches</p>
            <p className="text-xl font-bold text-gray-900">{totals?.total_matches || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Banned Users</p>
            <p className="text-xl font-bold text-red-600">{totals?.total_banned_users || 0}</p>
          </div>
        </div>

        {/* Daily Averages */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4">Daily Averages ({days} days)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500">Avg. Active Users</p>
              <p className="text-2xl font-bold text-blue-600">{avgDaily.active_users || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg. Registrations</p>
              <p className="text-2xl font-bold text-green-600">{avgDaily.registrations || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg. Listings/day</p>
              <p className="text-2xl font-bold text-amber-600">{avgDaily.listings || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg. Transactions/day</p>
              <p className="text-2xl font-bold text-purple-600">{avgDaily.transactions || 0}</p>
            </div>
          </div>
        </div>

        {/* Reports */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              Reports
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Total Reports</p>
              <p className="text-2xl font-bold text-gray-900">{totals?.total_reports || 0}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-500">Pending Review</p>
              <p className="text-2xl font-bold text-red-600">{totals?.pending_reports || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
